import { FeatureEnum } from '../../services/data/impl/cube/cubeSlice';
import { isFeatureEnum } from '../commonUtils';
import {
  ScanData,
  ScanDataEnum,
  ScanDataMeasureEnum,
} from './scanDataDefinitions';

/**
 * This function attempts to retrieve a value of the specified type from a ScanData object
 * for a given key. The key could be a string or a value from any enumeration. If the key
 * exists in the ScanData object and the associated value is of the expected type, it
 * returns the value. If the key does not exist or the associated value is not of the
 * expected type, it returns null.
 *
 * @param key - The key to look up in the ScanData object. It could be a string or a value from an enum.
 * @param scanData - The ScanData object from which to retrieve the value.
 * @param expectedType? - Optional. The expected type of the value. It should be either 'number' or 'string'.
 * @returns The value associated with the key if it is of the expected type, or null otherwise.
 *
 * Usage example:
 *
 *  const result1 = getValueFromScanData<number>(ScanDataEnum.raw_count, scanData, 'number');  // result1 is 10
 *
 *  const result2 = getValueFromScanData<string>('detection_id', scanData, 'string');  // result2 is 'abc123'
 *
 *  const result3 = getValueFromScanData<number>('non_existent_key', scanData, 'number');  // result3 is null
 *
 *  const result4 = getValueFromScanData<number>('some_key', scanData);  // result4 is 42 if some_key is a number
 */
export function getValueFromScanData<T extends string | number>(
  key: keyof ScanData | string | ScanDataMeasureEnum | ScanDataEnum,
  scanData: ScanData,
  expectedType?: 'string' | 'number'
): T | null {
  if (key in scanData) {
    const value = scanData[key as keyof ScanData];
    if (!expectedType || typeof value === expectedType) {
      return value as T;
    }
  }
  return null;
}

export const blueberrySortOrder = [
  //FeatureEnum.flower_not_clear,
  FeatureEnum.flower_closed,
  FeatureEnum.flower_open,
  FeatureEnum.flower_petalfall,
  FeatureEnum.flower,
  FeatureEnum.blueberry_Green,
  FeatureEnum.blueberry_Yellow,
  FeatureEnum.blueberry_Purple,
  FeatureEnum.blueberry_Blue,
];

/**
 * Orders an array of blueberries by their ripeness.
 * Keeps original array without mutations
 *
 * @param arrayToOrder - The array of blueberries to order.
 * @param blueberryKeyGetter - An optional function to extract the key used for ordering the blueberries.
 * @returns The ordered array of blueberries.
 */
export function orderBlueberriesByRipenessOrder<T>({
  arrayToOrder,
  blueberryKeyGetter,
}: {
  arrayToOrder: Array<T> | null;
  blueberryKeyGetter?: (a: T) => string;
}): Array<T> {
  if (!arrayToOrder) {
    return [];
  }

  return [...arrayToOrder].sort((a, b) => {
    const keyA = blueberryKeyGetter ? blueberryKeyGetter(a) : (a as string);
    const keyB = blueberryKeyGetter ? blueberryKeyGetter(b) : (b as string);

    if (keyA && isFeatureEnum(keyA) && keyB && isFeatureEnum(keyB)) {
      return (
        blueberrySortOrder.indexOf(keyA as FeatureEnum) -
        blueberrySortOrder.indexOf(keyB as FeatureEnum)
      );
    } else {
      return 0;
    }
  });
}
